import { shippingCarriers } from "@redotech/redo-model/carriers";
import { ReturnLabelQuantity } from "@redotech/redo-model/team";
import { Card } from "@redotech/redo-web/card";
import * as gridCss from "@redotech/redo-web/grid.module.css";
import { LabeledInput } from "@redotech/redo-web/labeled-input";
import {
  FormMultiSelectDropdown,
  SelectDropdown,
} from "@redotech/redo-web/select-dropdown";
import { FormSwitch } from "@redotech/redo-web/switch";
import { FormTextInput } from "@redotech/redo-web/text-input";
import { InputProvider, groupInput, input } from "@redotech/ui/form";
import { arrayEqual, stringEqual } from "@redotech/util/equal";
import classNames from "classnames";
import { memo } from "react";

const returnLabelQuantityOptions: ReturnLabelQuantity[] = [
  ReturnLabelQuantity.PER_RETURN,
  ReturnLabelQuantity.PER_ITEM,
  ReturnLabelQuantity.PER_FULFILLMENT,
];

function returnLabelQuantityDisplayName(quantity: ReturnLabelQuantity): string {
  switch (quantity) {
    case ReturnLabelQuantity.PER_RETURN:
      return "One label per return";
    case ReturnLabelQuantity.PER_ITEM:
      return "One label per item";
    case ReturnLabelQuantity.PER_FULFILLMENT:
      return "One label per fulfillment";
  }
}

export const shippingForm = groupInput({
  carrierOverride: input<string[]>({ equal: arrayEqual(stringEqual) }),
  insured: input<boolean>(),
  labelAdjustment: input<string>(),
  pickupEnabled: input<boolean>(),
  pickupEnabledForHeavyItems: input<boolean>(),
  pickupMaxLabelUpgradeRange: input<string>(),
  canEditReturnAddress: input<boolean>(),
  generateQrCodes: input<boolean>(),
  returnLabelQuantity: input<ReturnLabelQuantity>(),
  useFixedPackageDimensions: input<boolean>(),
  fixedPackageDimensions: groupInput({
    length: input<string>(),
    width: input<string>(),
    height: input<string>(),
    weight: input<string>(),
  }),
  refundExpiredLabels: input<boolean>(),
});

export type ShippingForm = InputProvider.Form<typeof shippingForm>;

export type ShippingValue = InputProvider.Value<typeof shippingForm>;

export const shippingDefault: ShippingValue = {
  carrierOverride: [],
  insured: false,
  labelAdjustment: "",
  pickupEnabled: false,
  pickupEnabledForHeavyItems: false,
  pickupMaxLabelUpgradeRange: "",
  canEditReturnAddress: false,
  generateQrCodes: false,
  returnLabelQuantity: ReturnLabelQuantity.PER_RETURN,
  useFixedPackageDimensions: false,
  fixedPackageDimensions: { length: "", width: "", height: "", weight: "" },
  refundExpiredLabels: false,
};

export const ShippingCard = memo(function ShippingCard({
  input,
  labelAdjustmentDefault,
}: {
  input: ShippingForm;
  labelAdjustmentDefault: string;
}) {
  const {
    carrierOverride,
    labelAdjustment,
    pickupEnabled,
    pickupEnabledForHeavyItems,
    pickupMaxLabelUpgradeRange,
    insured,
    canEditReturnAddress,
    generateQrCodes,
    returnLabelQuantity,
    useFixedPackageDimensions,
    fixedPackageDimensions,
    refundExpiredLabels,
  } = input.inputs;

  return (
    <Card title="Shipping">
      <div className={gridCss.grid}>
        <div className={classNames(gridCss.span12, gridCss.span6M)}>
          <FormMultiSelectDropdown
            description="Enabled shipping carriers"
            display={(value) => {
              if (!value.length || value.length === shippingCarriers.length) {
                return "All";
              }
              const names = value.map(
                (value) =>
                  shippingCarriers.find((carrier) => carrier.value === value)
                    ?.name,
              );
              return names.join(", ");
            }}
            input={carrierOverride}
            label="Carriers"
            options={shippingCarriers.map((carrier) => carrier.value)}
          >
            {(option) =>
              shippingCarriers.find((carrier) => carrier.value === option)?.name
            }
          </FormMultiSelectDropdown>
        </div>
        <div className={classNames(gridCss.span12, gridCss.span6M)}>
          <FormSwitch input={insured} label="Shipping insurance">
            Enable shipping insurance. Increases label price by 0.5% of items
            value (min $0.50).
          </FormSwitch>
        </div>
        <div className={classNames(gridCss.span12, gridCss.span6M)}>
          <FormTextInput
            description="Adjustment to the label price charged to merchants and customers"
            input={labelAdjustment}
            label="Label adjustment"
            placeholder={labelAdjustmentDefault}
            prefix="$"
          />
        </div>
        <div className={classNames(gridCss.span12, gridCss.span6M)}>
          <FormSwitch input={pickupEnabled} label="Return pickup">
            Offer return package pickup, for the cost of label upgrade plus $2
          </FormSwitch>
        </div>
        {pickupEnabled.value && (
          <>
            <div className={classNames(gridCss.span12, gridCss.span6M)}>
              <FormSwitch
                input={pickupEnabledForHeavyItems}
                label="Allow package pickup for heavy items"
              >
                Allow package pickup for all items regardless of weight limits
                (over 9 lbs)
              </FormSwitch>
            </div>
            <div className={classNames(gridCss.span12, gridCss.span6M)}>
              <FormTextInput
                description="Maximum range for package pickup label upgrade"
                input={pickupMaxLabelUpgradeRange}
                label="Pickup max label upgrade range"
                min={0.01}
                prefix="$"
                step="0.01"
                type="number"
              />
            </div>
          </>
        )}
        <div className={classNames(gridCss.span12, gridCss.span6M)}>
          <FormSwitch
            input={canEditReturnAddress}
            label="Change return address"
          >
            Allows Merchant to change return address during return approval
          </FormSwitch>
        </div>
        <div className={classNames(gridCss.span12, gridCss.span6M)}>
          <FormSwitch input={generateQrCodes} label="Generate QR codes">
            If we create a label using a carrier that supports QR codes, we will
            generate QR codes for the return
          </FormSwitch>
        </div>
        <div className={classNames(gridCss.span12, gridCss.span6M)}>
          <FormSwitch
            input={refundExpiredLabels}
            label="Refund expired return labels"
          >
            Automatically refund shipping labels when returns expire
          </FormSwitch>
        </div>
        <div className={classNames(gridCss.span12, gridCss.span6M)}>
          <LabeledInput
            description="Controls how many return labels are created per return. Need finance approval before allowing multiple labels per return"
            label="Return label quantity"
          >
            <SelectDropdown
              options={returnLabelQuantityOptions}
              value={returnLabelQuantity.value}
              valueChange={returnLabelQuantity.setValue}
            >
              {returnLabelQuantityDisplayName}
            </SelectDropdown>
          </LabeledInput>
        </div>
        <div className={gridCss.span12}>
          <FormSwitch
            input={useFixedPackageDimensions}
            label="Use fixed package dimensions"
          >
            Use standard dimensions for all return labels instead of calculating
            from product weight
          </FormSwitch>
        </div>
        {useFixedPackageDimensions.value && (
          <>
            <div className={classNames(gridCss.span12, gridCss.span3M)}>
              <FormTextInput
                input={fixedPackageDimensions.inputs.length}
                label="Length (in)"
                step="0.1"
                type="number"
              />
            </div>
            <div className={classNames(gridCss.span12, gridCss.span3M)}>
              <FormTextInput
                input={fixedPackageDimensions.inputs.width}
                label="Width (in)"
                step="0.1"
                type="number"
              />
            </div>
            <div className={classNames(gridCss.span12, gridCss.span3M)}>
              <FormTextInput
                input={fixedPackageDimensions.inputs.height}
                label="Height (in)"
                step="0.1"
                type="number"
              />
            </div>
            <div className={classNames(gridCss.span12, gridCss.span3M)}>
              <FormTextInput
                input={fixedPackageDimensions.inputs.weight}
                label="Weight (oz)"
                step="0.1"
                type="number"
              />
            </div>
          </>
        )}
      </div>
    </Card>
  );
});
